import { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { Link } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/flavours/glitch/store';
import { fetchTrendingStatuses } from 'flavours/glitch/actions/trends';
import api from 'flavours/glitch/api';
import type { ApiStatusJSON } from 'flavours/glitch/api_types/statuses';
import { trendsEnabled } from 'flavours/glitch/initial_state';
import { getStatusList } from 'flavours/glitch/selectors';

import classes from './index.module.scss';

const TRENDING_REFRESH_INTERVAL = 15 * 60 * 1000;
const TOP_FAVOURITES_LIMIT = 3;

interface TopFavouriteStatus {
  id: string;
  acct: string;
  favouritesCount: number;
  preview: string;
}

type TopFavouriteTab = 'trending' | 'all-time';

const getPreviewText = (status: {
  spoiler_text?: string;
  search_index?: string;
  content?: string;
}) => {
  const spoilerText = status.spoiler_text;
  if (typeof spoilerText === 'string' && spoilerText.trim().length > 0) {
    return spoilerText.trim();
  }

  const searchIndex = status.search_index;
  if (typeof searchIndex === 'string' && searchIndex.trim().length > 0) {
    return searchIndex.trim();
  }

  const content = typeof status.content === 'string' ? status.content : '';
  const plainText = content
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const normalizedText = plainText.replace(/([@#])\s+(\S+)/g, '$1$2');
  return normalizedText;
};

export const TopFavouritesPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const [topAllTime, setTopAllTime] = useState<TopFavouriteStatus[]>([]);
  const [selectedTab, setSelectedTab] = useState<TopFavouriteTab>('trending');

  const topTrending = useAppSelector((state) => {
    const posts: TopFavouriteStatus[] = [];

    getStatusList(state, 'trending').forEach((statusId) => {
      const status = state.statuses.get(statusId);

      if (!status) {
        return;
      }

      const accountId = status.get('account') as string | undefined;
      const account = accountId ? state.accounts.get(accountId) : undefined;
      const acct = account?.get('acct');

      if (typeof acct !== 'string') {
        return;
      }

      const spoilerText = status.get('spoiler_text');
      const searchIndex = status.get('search_index');
      const previewSource =
        typeof spoilerText === 'string' && spoilerText.trim().length > 0
          ? spoilerText
          : searchIndex;

      posts.push({
        id: statusId,
        acct,
        favouritesCount: Number(status.get('favourites_count') ?? 0),
        preview:
          typeof previewSource === 'string' && previewSource.trim().length > 0
            ? previewSource.trim()
            : `@${acct}`,
      });
    });

    return posts
      .sort((left, right) => right.favouritesCount - left.favouritesCount)
      .slice(0, TOP_FAVOURITES_LIMIT);
  });

  useEffect(() => {
    dispatch(fetchTrendingStatuses());

    const refreshInterval = setInterval(() => {
      dispatch(fetchTrendingStatuses());
    }, TRENDING_REFRESH_INTERVAL);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [dispatch]);

  useEffect(() => {
    let aborted = false;

    const fetchTopAllTime = () => {
      api()
        .get('/api/v1/timelines/public', { params: { limit: 40, local: true } })
        .then(({ data }) => {
          if (aborted) {
            return;
          }

          const statuses = (Array.isArray(data) ? data : []) as ApiStatusJSON[];
          const posts = statuses
            .filter((status) => typeof status.account.acct === 'string')
            .map((status) => ({
              id: status.id,
              acct: status.account.acct,
              favouritesCount: status.favourites_count,
              preview: getPreviewText(status) || `@${status.account.acct}`,
            }))
            .sort((left, right) => right.favouritesCount - left.favouritesCount)
            .slice(0, TOP_FAVOURITES_LIMIT);

          setTopAllTime(posts);
        })
        .catch(() => {
          if (!aborted) {
            setTopAllTime([]);
          }
        });
    };

    fetchTopAllTime();

    const refreshInterval = setInterval(
      fetchTopAllTime,
      TRENDING_REFRESH_INTERVAL,
    );

    return () => {
      aborted = true;
      clearInterval(refreshInterval);
    };
  }, []);

  const hasTrendingTab = trendsEnabled && topTrending.length > 0;

  const activeTab: TopFavouriteTab =
    hasTrendingTab && selectedTab === 'trending' ? 'trending' : 'all-time';

  const handleTrendingTabClick = useCallback(() => {
    setSelectedTab('trending');
  }, []);

  const handleAllTimeTabClick = useCallback(() => {
    setSelectedTab('all-time');
  }, []);

  const activeStatuses = activeTab === 'trending' ? topTrending : topAllTime;

  return (
    <aside className={classes.root}>
      <h2 className={classes.heading}>
        <Link to={activeTab === 'trending' ? '/explore' : '/public/local'}>
          <FormattedMessage
            id='compose_panel.top_favourites'
            defaultMessage='Most favorited posts'
          />
        </Link>
      </h2>

      <div className={classes.tabs} role='tablist'>
        {hasTrendingTab && (
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'trending'}
            className={classes.tab}
            data-active={activeTab === 'trending'}
            onClick={handleTrendingTabClick}
          >
            <FormattedMessage
              id='compose_panel.top_favourites.trending'
              defaultMessage='Top trending'
            />
          </button>
        )}

        <button
          type='button'
          role='tab'
          aria-selected={activeTab === 'all-time'}
          className={classes.tab}
          data-active={activeTab === 'all-time'}
          onClick={handleAllTimeTabClick}
        >
          <FormattedMessage
            id='compose_panel.top_favourites.all_time'
            defaultMessage='Top all time'
          />
        </button>
      </div>

      <ul className={classes.list}>
        {activeStatuses.map((status) => (
          <li key={status.id} className={classes.item}>
            <Link
              className={classes.itemLink}
              to={`/@${status.acct}/${status.id}`}
            >
              <p className={classes.meta}>
                <span>@{status.acct}</span>
                <span>
                  <FormattedMessage
                    id='status.favourites_count'
                    defaultMessage='{count, plural, one {{counter} favorite} other {{counter} favorites}}'
                    values={{
                      count: status.favouritesCount,
                      counter: status.favouritesCount,
                    }}
                  />
                </span>
              </p>

              <p className={classes.preview}>{status.preview}</p>
            </Link>
          </li>
        ))}

        {activeStatuses.length === 0 && (
          <li className={classes.item}>
            <div className={classes.empty}>
              <FormattedMessage
                id='compose_panel.top_favourites.empty'
                defaultMessage='No posts available yet.'
              />
            </div>
          </li>
        )}
      </ul>
    </aside>
  );
};
